import { useState, useEffect, useRef, useCallback } from "react";
import { SoundLibrary } from "../services/SoundLibrary";
import {
    InstrumentName,
    Note,
    PlaybackOptions,
    IntervalPlaybackOptions,
    SoundLibraryConfig,
    LoadProgress,
    SoundLibraryError,
} from "../types/sound";

interface UseSoundLibraryReturn {
    soundLibrary: SoundLibrary;
    loadInstrument: (instrument: InstrumentName) => Promise<void>;
    loadMultipleInstruments: (instruments: InstrumentName[]) => Promise<void>;
    playNote: (instrument: InstrumentName, note: Note, options?: PlaybackOptions) => Promise<void>;
    playChord: (
        instrument: InstrumentName,
        notes: Note[],
        options?: PlaybackOptions
    ) => Promise<void>;
    playInterval: (
        instrument: InstrumentName,
        note1: Note,
        note2: Note,
        options?: IntervalPlaybackOptions
    ) => Promise<void>;
    playSequence: (
        instrument: InstrumentName,
        notes: Note[],
        options?: PlaybackOptions & { noteDelayMs?: number }
    ) => Promise<void>;
    stopAll: () => void;
    clearAbort: () => void;
    isInstrumentLoaded: (instrument: InstrumentName) => boolean;
    isLoading: boolean;
    loadingProgress: LoadProgress | null;
    error: SoundLibraryError | null;
    clearError: () => void;
}

export function useSoundLibrary(config?: SoundLibraryConfig): UseSoundLibraryReturn {
    const soundLibraryRef = useRef<SoundLibrary | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingProgress, setLoadingProgress] = useState<LoadProgress | null>(null);
    const [error, setError] = useState<SoundLibraryError | null>(null);

    useEffect(() => {
        if (!soundLibraryRef.current) {
            soundLibraryRef.current = new SoundLibrary(config);
        }

        return () => {
            if (soundLibraryRef.current) {
                soundLibraryRef.current.dispose();
            }
        };
    }, [config]);

    const clearError = useCallback(() => {
        setError(null);
    }, []);

    const handleError = useCallback((err: any) => {
        if (err && typeof err === "object" && "code" in err) {
            setError(err as SoundLibraryError);
        } else {
            setError({
                code: "LOAD_ERROR",
                message: err?.message || "Unknown error occurred",
                originalError: err,
            });
        }
    }, []);

    const loadInstrument = useCallback(
        async (instrument: InstrumentName) => {
            if (!soundLibraryRef.current) return;

            try {
                setIsLoading(true);
                setError(null);

                await soundLibraryRef.current.loadInstrument(instrument, (progress) => {
                    setLoadingProgress(progress);
                });
            } catch (err) {
                handleError(err);
            } finally {
                setIsLoading(false);
                setLoadingProgress(null);
            }
        },
        [handleError]
    );

    const loadMultipleInstruments = useCallback(
        async (instruments: InstrumentName[]) => {
            if (!soundLibraryRef.current) return;

            try {
                setIsLoading(true);
                setError(null);

                await soundLibraryRef.current.loadMultipleInstruments(instruments, (progress) => {
                    setLoadingProgress(progress);
                });
            } catch (err) {
                handleError(err);
            } finally {
                setIsLoading(false);
                setLoadingProgress(null);
            }
        },
        [handleError]
    );

    const playNote = useCallback(
        async (instrument: InstrumentName, note: Note, options?: PlaybackOptions) => {
            if (!soundLibraryRef.current) return;

            try {
                setError(null);
                await soundLibraryRef.current.playNote(instrument, note, options);
            } catch (err) {
                handleError(err);
            }
        },
        [handleError]
    );

    const playChord = useCallback(
        async (instrument: InstrumentName, notes: Note[], options?: PlaybackOptions) => {
            if (!soundLibraryRef.current) return;

            try {
                setError(null);
                await soundLibraryRef.current.playChord(instrument, notes, options);
            } catch (err) {
                handleError(err);
            }
        },
        [handleError]
    );

    const playInterval = useCallback(
        async (
            instrument: InstrumentName,
            note1: Note,
            note2: Note,
            options?: IntervalPlaybackOptions
        ) => {
            if (!soundLibraryRef.current) return;

            try {
                setError(null);
                await soundLibraryRef.current.playInterval(instrument, note1, note2, options);
            } catch (err) {
                handleError(err);
            }
        },
        [handleError]
    );

    const playSequence = useCallback(
        async (
            instrument: InstrumentName,
            notes: Note[],
            options?: PlaybackOptions & { noteDelayMs?: number }
        ) => {
            if (!soundLibraryRef.current) return;

            try {
                setError(null);
                await soundLibraryRef.current.playSequence(instrument, notes, options);
            } catch (err) {
                handleError(err);
            }
        },
        [handleError]
    );

    const isInstrumentLoaded = useCallback((instrument: InstrumentName) => {
        return soundLibraryRef.current?.isInstrumentLoaded(instrument) || false;
    }, []);

    const stopAll = useCallback(() => {
        soundLibraryRef.current?.stopAll();
    }, []);

    const clearAbort = useCallback(() => {
        soundLibraryRef.current?.clearAbort();
    }, []);

    return {
        soundLibrary: soundLibraryRef.current!,
        loadInstrument,
        loadMultipleInstruments,
        playNote,
        playChord,
        playInterval,
        playSequence,
        stopAll,
        clearAbort,
        isInstrumentLoaded,
        isLoading,
        loadingProgress,
        error,
        clearError,
    };
}
